import { Dispatch, ReactNode, SetStateAction } from 'react';

import { EFinalPageType, ESocketEvent } from 'shared/enum';

export interface IOptions {
  value?: string;
  label?: string;
  id?: string;
}

/* Questions */
export interface IQuestionsBase {
  id: number;
  order: number;
  definition?: null;
  defaultOptionId: number;
  value: string;
  questionOptions: IQuestionOptionRs[];
}

export interface IQuestionRs extends IQuestionsBase {
  questionOptions: IQuestionOptionRs[];
}

export interface IQuestion extends IQuestionsBase {
  questionOptions: IQuestionOption[];
}

export interface IQuestionOptionRs {
  id: number;
  order: number;
  value: string;
}

export interface IQuestionOption extends IQuestionOptionRs {
  selected: boolean;
}

export interface IQuestionRes {
  questionnaires: Array<IQuestionsBase>;
}

/* InsuranceProducts Product */
export interface ICost {
  truncated: string;
  detailed: string;
}
export interface ICrossSellData {
  amount?: string;
  code: string;
  generalInformation?: string;
  note?: string;
  title?: string;
  cost?: ICost;
  truncated?: string;
  detail?: string;
  summary?: string;
}

/* Product Info */
interface IDetailItem {
  key?: string;
  title: string;
  info: string;
}
export interface IProductData {
  code?: string;
  conditions?: string[];
  description?: string;
  details?: IDetailItem[];
  icon?: string;
  title?: string;
  info?: string;
}

export interface IGiftCardInfo {
  creditLimit?: string;
  bonus: string;
  generalInformation: string;
  gracePeriod?: string;
  partners?: string;
  serviceFee?: string;
  title?: string;
}

/* Validation */
export interface IValidationError {
  code: string;
  message: string;
}

export type StateActionDispatcher<S> = Dispatch<SetStateAction<S>>;

/* Delivery */
export interface IBranchFormData {
  branch: IOptions;
  time: IOptions;
  workDate: string;
}

export interface IBranchData {
  id: string;
  serviceId: string;
  name: string;
}

export interface ICurrierFormData {
  deliveryDate: string;
  deliveryTime: IOptions;
}

export interface ICurrierDate {
  selectedDay: string;
  availableTime: string;
  startTime: number;
  endTime: number;
  availableDays: number;
  interval: number;
}

export interface IDeliveryBranchReq {
  branches: Array<IBranchData>;
}
export interface IDeliveryWorkDayReq {
  workDays: Array<string>;
}
export interface IDeliveryWorkHourReq {
  workHours: Array<IBranchWorkHours>;
}
export interface IBranchWorkHours {
  time: string;
  active: boolean;
}

export interface IDeliveryReq {
  city: string;
  language: string;
  lat: number;
  lon: number;
  min_preparation_time_minutes: number;
  pin: string | null;
  post_code: string;
  scheduled_dropoff_time: string | null;
  street: string;
}

export interface IDeliveryResp {
  id: string;
  scheduledTime: string | null;
  isBinding: boolean;
}

export interface IComplateDeliveryReq {
  processId: string | null;
  branchId: string | undefined;
  shipmentAddress: string | undefined;
  time: string | undefined | null;
  date: string | null;
}

export interface IVenueData {
  address: string;
  branch: string;
  branchCode: string;
  lat: number;
  lon: number;
  venueId: string;
}

/* Product Details */
export interface IProductDetails {
  code: string;
  monthlyPayment: number;
  period: number;
  percent: number;
  amount: string;
  shipmentMethod: string;
  shipmentAddress: string;
  shipmentTime: string;
}

export interface ICustomerDetails {
  maskedFullName: string;
  additionalNumber1: string;
  additionalNumber2: string;
  factualAddress: string;
  address: string;
}

export interface ITransferDetails {
  maskedSalaryPan: string;
}
export interface IDVSDetails {
  dvsLink: string;
  status: string;
}

interface IDeliveryDetails {
  branchServiceId: number;
}

export interface IErrorDetails {
  type: EFinalPageType;
  code: string;
  text: string;
  link: string;
  title: string;
}

interface IOrderOffersDetails {
  id: string;
  minAmount: number;
  maxAmount: number;
  issuingAmount: number;
  refinancingAmount: number;
  commission: number;
  bankGuaranteeCommission: number;
  creditServiceCommission: number;
  cashCommissionRate: number;
  bankGuaranteeCommissionRate: number;
  cashCreditServiceCommissionRate: number;
  interestRate: number;
  availableLoanAmount: number;
  period: string;
}
interface IOrderOffersCalculator {
  additionalProp1: Array<IOrderOffersDetails>;
}

export interface IAdvanceDebt {
  value: number;
  currency: string;
}
export interface IDebtAmount {
  refinancingAmount: number;
  advanceDebt: IAdvanceDebt;
  productDebtAmount: number;
}
interface ICalculatorDetails {
  minAmount: number;
  maxAmount: number;
  duration: number;
  percent: number;
  orderOffers?: IOrderOffersCalculator;
  refinancingAmount?: number;
  advanceDebt?: IAdvanceDebt;
  giftCardSelected: boolean;
}
export interface IDetails {
  processId: string;
  product: IProductDetails;
  calculator: ICalculatorDetails;
  customer: ICustomerDetails;
  transfer: ITransferDetails;
  dvs: IDVSDetails;
  delivery: IDeliveryDetails;
  step: ESocketEvent;
  progressingStatus: string;
  error: IErrorDetails;
}

export interface IRegions {
  name: string;
  subregion: [];
}

export interface ITransferReq {
  processId: string | null;
  transferOption: string;
  transferPan?: string;
}

export interface ICustomerInfoReq {
  processId: string | null;
  factualAddress: string | undefined;
  additionalNumber1: string;
  additionalNumber2: string;
  fatca: boolean;
}

export interface ICreditFormData {
  accept: boolean;
  additionalNumber1: string;
  additionalNumber2: string;
  hasYesAnswer: boolean;
  prefix1: IOptions;
  prefix2: IOptions;
  factualAddress: string;
}

export interface IUTMReq {
  data: Record<string, string>[];
  groupId: string;
}

export interface IOrderDetails {
  amount?: number;
  crossSellCodes?: Array<string>;
  maxAmount?: number;
  minAmount?: number;
  processId?: string | null;
  selectedAmount?: number;
  selectedOfferId?: string;
  period?: number;
  interestRate?: number;
  bankGuaranteeCommissionRate?: number;
  cashCommissionRate?: number;
  cashCreditServiceCommissionRate?: number;
}

/* OTP */
export interface IOTP {
  processId: string | null;
  phoneNumber: string | null;
}

export interface IVerifyOtpResponse {
  status: boolean;
  authorizationCode: string;
}

export interface IAuthLoginRequest {
  grantType: string;
  code: string;
  clientId: string;
  codeVerifier: string;
}

export interface IAuthLoginResponse {
  accessToken: string;
  refreshToken: string;
}

export enum EClient {
  ID = 'kb_website',
  CODE = 'authorization_code',
}

/* Final Page */
export interface ISuccessData {
  title?: string;
  amount?: string;
  shipmentMethod?: string;
  shipmentAddress?: string;
  shipmentTime?: string;
  free?: string;
}

export interface IFinalProcessInfoDetails {
  image: string;
  title: string;
  content: React.ReactNode;
  btnText: string;
  btnLink: string;
  hasHomeLink: boolean;
  hasError?: boolean;
  homeLink?: string;
}

// Life Insurance

export interface ITariff {
  duration: number;
  tariff: number;
}

export interface IInsurance {
  id: number;
  name: string;
  icon: string;
  minInsuranceFee?: number;
  tariffs: ITariff[];
}

export interface ISelectedInsurance {
  amount?: number | null;
  checked: boolean;
  id: number;
  imageName?: string;
  label: string;
  month?: string;
  name: string;
  percent?: number | null;
  title: string | ReactNode;
  value: string;
}

export interface ICashBackCardDetails {
  name: string;
  value: string;
  isFree?: boolean;
}

// CreditCalculyator

export interface LoanOffer {
  id: string;
  minAmount: number;
  maxAmount: number;
  issuingAmount: number;
  refinancingAmount: number;
  commission: number;
  bankGuaranteeCommission: number;
  creditServiceCommission: number;
  cashCommissionRate: number;
  bankGuaranteeCommissionRate: number;
  cashCreditServiceCommissionRate: number;
  interestRate: number;
  availableLoanAmount: number;
  period: string;
  giftCardAvailable: boolean;
  visibleUi: boolean;
  unAdjustedInterestRate: number;
}

interface InsuranceTariff {
  duration: number;
  tariff: number;
}

interface InsuranceDetail {
  id: number;
  name: string;
  icon: string;
  minInsuranceFee?: number;
  tariffs: InsuranceTariff[];
}

interface Insurance {
  insuranceDetails: InsuranceDetail[];
  commonInfo: string;
  useInsuranceService: boolean;
}

interface CrossSell {
  code: string;
  summary: string;
  title: string;
  generalInformation: string;
}

interface Calculator {
  customerDebts: Record<string, unknown>;
  giftCardSelected: boolean;
  orderOffers: {
    [period: string]: LoanOffer[];
  };
  insurance: Insurance;
}

interface Product {
  code: string;
  productGeneralType: string;
}
export interface LoanCalculationData {
  processId: string;
  product: Product;
  calculator: Calculator;
  crossSells: CrossSell[];
}

export enum ToastType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning',
}

export enum ErrorCode {
  VENUE_CLOSED = 'VENUE_CLOSED',
  DROPOFF_OUTSIDE_OF_DELIVERY_AREA = 'DROPOFF_OUTSIDE_OF_DELIVERY_AREA',
}

export interface IToast {
  id: string;
  title: string;
  type: ToastType;
}

export interface ISubmitQuestionnaireVerification {
  processId: string | null;
  additionalNumber1: string;
  additionalNumber2: string;
  fatca: null;
  factualAddress: string;
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface ITranslatedError {
  message?: string;
  type: string;
}

export interface IErrorValidation {
  message: string;
  code: string;
}